import React, { useEffect, useState } from 'react';

const MAX_ENTRIES = 300;
const buffer = [];
const listeners = new Set();
let consolePatched = false;

const now = () => {
  const d = new Date();
  const pad = (n, w = 2) => String(n).padStart(w, '0');
  return `${pad(d.getHours())}:${pad(d.getMinutes())}:${pad(d.getSeconds())}.${pad(d.getMilliseconds(), 3)}`;
};

const safeStringify = (val) => {
  if (val === undefined) return 'undefined';
  if (val === null) return 'null';
  if (val instanceof Error) return `${val.name}: ${val.message}`;
  if (typeof val === 'string') return val;
  try {
    return JSON.stringify(val, (_, v) => {
      if (v instanceof Error) return `${v.name}: ${v.message}`;
      if (typeof v === 'function') return `[fn ${v.name || 'anon'}]`;
      return v;
    }, 2);
  } catch (e) {
    try { return String(val); } catch (_) { return '[unstringifiable]'; }
  }
};

const push = (level, source, msg, extra) => {
  const entry = {
    id: buffer.length ? buffer[buffer.length - 1].id + 1 : 1,
    time: now(),
    level,
    source,
    msg,
    extra: extra !== undefined ? safeStringify(extra) : null,
  };
  buffer.push(entry);
  if (buffer.length > MAX_ENTRIES) buffer.shift();
  listeners.forEach((fn) => {
    try { fn(); } catch (_) {}
  });
};

export const logDebug = (source, msg, extra) => push('log', source, msg, extra);
export const logWarn = (source, msg, extra) => push('warn', source, msg, extra);
export const logError = (source, msg, extra) => push('error', source, msg, extra);

const patchConsole = () => {
  if (consolePatched || typeof window === 'undefined') return;
  consolePatched = true;
  const orig = {
    log: console.log.bind(console),
    warn: console.warn.bind(console),
    error: console.error.bind(console),
  };
  const wrap = (level, fn) => (...args) => {
    try {
      const first = args[0];
      const rest = args.slice(1);
      push(level, 'console', typeof first === 'string' ? first : safeStringify(first), rest.length ? rest : undefined);
    } catch (_) {}
    return fn(...args);
  };
  console.log = wrap('log', orig.log);
  console.warn = wrap('warn', orig.warn);
  console.error = wrap('error', orig.error);

  window.addEventListener('error', (e) => {
    push('error', 'window', e?.message || 'window error', {
      filename: e?.filename,
      lineno: e?.lineno,
      colno: e?.colno,
      error: e?.error?.stack || e?.error?.message || null,
    });
  });
  window.addEventListener('unhandledrejection', (e) => {
    const r = e?.reason;
    push('error', 'promise', r?.message || 'unhandled rejection', {
      name: r?.name,
      stack: r?.stack,
      value: safeStringify(r),
    });
  });
};

export const isDebugEnabled = () => {
  if (typeof window === 'undefined') return false;
  try {
    const qs = new URLSearchParams(window.location.search);
    if (qs.get('debug') === '1') return true;
    return sessionStorage.getItem('ar_debug') === '1';
  } catch (_) {
    return false;
  }
};

const LEVEL_COLORS = {
  log: '#93c5fd',
  warn: '#fbbf24',
  error: '#f87171',
};

export const ARDebugPanel = () => {
  const [enabled, setEnabled] = useState(isDebugEnabled());
  const [open, setOpen] = useState(false);
  const [, setTick] = useState(0);

  useEffect(() => {
    if (!enabled) return;
    patchConsole();
    const rerender = () => setTick((n) => n + 1);
    listeners.add(rerender);
    return () => listeners.delete(rerender);
  }, [enabled]);

  useEffect(() => {
    if (typeof window === 'undefined') return;
    window.__enableArDebug = () => {
      try { sessionStorage.setItem('ar_debug', '1'); } catch (_) {}
      setEnabled(true);
    };
    window.__arLogs = () => buffer.slice();
  }, []);

  if (!enabled) return null;

  const copyAll = async () => {
    const text = buffer.map((e) => {
      const head = `[${e.time}] ${e.level.toUpperCase()} ${e.source}: ${e.msg}`;
      return e.extra ? `${head}\n${e.extra}` : head;
    }).join('\n\n');
    try {
      await navigator.clipboard.writeText(text);
      alert('Logs copied to clipboard');
    } catch (_) {
      const ta = document.createElement('textarea');
      ta.value = text;
      document.body.appendChild(ta);
      ta.select();
      try { document.execCommand('copy'); alert('Logs copied'); } catch (__) { alert('Copy failed'); }
      document.body.removeChild(ta);
    }
  };

  const clear = () => {
    buffer.length = 0;
    setTick((n) => n + 1);
  };

  const buttonStyle = {
    position: 'fixed',
    right: 12,
    bottom: 12,
    zIndex: 2147483647,
    minWidth: 44,
    height: 44,
    padding: '0 12px',
    borderRadius: 22,
    border: 'none',
    background: '#111827',
    color: '#f9fafb',
    fontSize: 13,
    fontWeight: 600,
    boxShadow: '0 4px 12px rgba(0,0,0,0.35)',
  };

  if (!open) {
    return (
      <button style={buttonStyle} onClick={() => setOpen(true)} aria-label="Show AR debug logs">
        Log ({buffer.length})
      </button>
    );
  }

  const panelStyle = {
    position: 'fixed',
    inset: 0,
    zIndex: 2147483647,
    background: 'rgba(17,24,39,0.96)',
    color: '#f9fafb',
    display: 'flex',
    flexDirection: 'column',
    fontFamily: 'ui-monospace, SFMono-Regular, Menlo, monospace',
    fontSize: 12,
  };

  const headerStyle = {
    display: 'flex',
    gap: 8,
    padding: '10px 12px',
    borderBottom: '1px solid #374151',
    background: '#0b1220',
  };

  const btnStyle = {
    background: '#1f2937',
    color: '#f9fafb',
    border: '1px solid #374151',
    borderRadius: 6,
    padding: '6px 10px',
    fontSize: 12,
  };

  const listStyle = {
    flex: 1,
    overflowY: 'auto',
    padding: 8,
    WebkitOverflowScrolling: 'touch',
  };

  return (
    <div style={panelStyle}>
      <div style={headerStyle}>
        <strong style={{ marginRight: 'auto' }}>AR Debug ({buffer.length})</strong>
        <button style={btnStyle} onClick={copyAll}>Copy</button>
        <button style={btnStyle} onClick={clear}>Clear</button>
        <button style={btnStyle} onClick={() => setOpen(false)}>Close</button>
      </div>
      <div style={listStyle}>
        {buffer.length === 0 ? (
          <div style={{ color: '#9ca3af', padding: 8 }}>No logs yet.</div>
        ) : (
          buffer.map((e) => (
            <div key={e.id} style={{ borderBottom: '1px solid #1f2937', padding: '6px 0' }}>
              <div style={{ color: LEVEL_COLORS[e.level] || '#f9fafb' }}>
                [{e.time}] <strong>{e.source}</strong> · {e.msg}
              </div>
              {e.extra ? (
                <pre style={{ margin: '4px 0 0', whiteSpace: 'pre-wrap', wordBreak: 'break-word', color: '#d1d5db' }}>
                  {e.extra}
                </pre>
              ) : null}
            </div>
          ))
        )}
      </div>
    </div>
  );
};
